import get from 'lodash/get';
import {EventEmitter} from 'events';
import {getRootStore} from '../../app.bootstrap';

export interface ISnapshotReminderSettings {
    roomId: string;
    enabled: boolean;
    maxTimes: number;
    timeout: number;
}

export class TsSnapshotReminderService extends EventEmitter {
    private _settings?: ISnapshotReminderSettings;
    private _isVideoFocused: boolean = true;
    private _isVideoReady: boolean = false;
    private _isActive: boolean = false;
    private _timesPopped: number = 0;
    private _timeElapsedMs: number = 0;
    private _timer: NodeJS.Timeout | null = null;
    private _startTimestamp?: number;
    private _initialized: boolean = false;

    public pop: boolean = false;

    private readonly useNewInterface: boolean;
    private readonly eventLog: any;
    private readonly browserUtilsService: any;

    constructor() {
        super();

        this.useNewInterface = getRootStore().tsInterfaceHelperService.useNewInterface;
        this.eventLog = getRootStore().eventLogService;
        this.browserUtilsService = getRootStore().browserUtilsService;
    }

    init(settings: ISnapshotReminderSettings): void {
        if (this.browserUtilsService.getFromSessionStorage(`agentSnapshotReminderPopDisabled_${settings.roomId}`)) {
            return;
        }

        this._settings = settings;

        this._isVideoFocused = true;
        this._isVideoReady = false;
        this._isActive = this.useNewInterface && get(this._settings, 'enabled');
        this._timesPopped = 0;
        this._timeElapsedMs = 0;
        this._timer = null;

        this.hide();

        this._initialized = true;
    }

    hide(): void {
        this.pop = false;

        // Rewind timer if needed
        if (this._isActive) {
            this._updateTimer();
        }
    }

    show(): void {
        if (!this._isActive) {
            return;
        }

        this._timesPopped++;
        this.pop = true;

        this.emit('snapshotPopChanges', this.pop);

        this.eventLog.snapshotReminderShown({time: this._timesPopped, maxTimes: this._settings!.maxTimes});

        if (this._timesPopped >= this._settings!.maxTimes) {
            this._disableReminder();
        }
    }

    cleanup(): void {
        if (!this._initialized) {
            return;
        }

        this._isActive = false;

        this._initialized = false;
    }

    videoReady(isReady: boolean): void {
        if (!this._isActive) {
            return;
        }

        this._isVideoReady = isReady;

        this._updateTimer();
    }

    snapshotTaken(): void {
        if (!this._isActive) {
            return;
        }

        this._disableReminder();

        this._updateTimer();

        this.pop = false;

        this.emit('snapshotPopChanges', this.pop);

        this.emit('snapshotTaken');
    }

    videoFocusChanged(isFocused: boolean): void {
        if (!this._isActive) {
            return;
        }

        this._isVideoFocused = isFocused;

        this._updateTimer();
    }

    private _updateTimer(): void {
        const shouldBeTiming = this._isVideoFocused && this._isVideoReady;

        if (shouldBeTiming && !this._timer) {
            this._startTimestamp = Date.now();
            this._timer = setTimeout(
                () => {
                    this._timer = null;
                    this._timeElapsedMs = 0;

                    this.show();
                },
                this._settings!.timeout * 1000 - this._timeElapsedMs
            );
        } else if (!shouldBeTiming && this._timer) {
            clearTimeout(this._timer);
            this._timer = null;

            if (!this._isVideoFocused) {
                this._timeElapsedMs = 0;
            } else {
                this._timeElapsedMs += Date.now() - (this._startTimestamp || 0);
            }
        }
    }

    private _disableReminder(): void {
        this._isActive = false;

        this.browserUtilsService.saveToSessionStorage(
            `agentSnapshotReminderPopDisabled_${this._settings!.roomId}`,
            true
        );
    }
}
